import { z } from 'zod';
import { Email, Id, PermissionMask, Timestamp, Uri, MemberStatus } from './common.js';
import { User } from './users.js';
import { Workspace } from './workspaces.js';
import { Role } from './members.js';

export const SignupRequest = z.object({
  email: Email,
  password: z.string().min(10).max(256),
  display_name: z.string().min(1).max(120),
  invite_token: z.string().optional(),
});
export type SignupRequest = z.infer<typeof SignupRequest>;

export const LoginRequest = z.object({
  email: Email,
  password: z.string(),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const MagicLinkRequest = z.object({
  email: Email,
  redirect_path: z.string().optional(),
});
export type MagicLinkRequest = z.infer<typeof MagicLinkRequest>;

export const MagicLinkVerifyRequest = z.object({
  token: z.string().min(16),
});
export type MagicLinkVerifyRequest = z.infer<typeof MagicLinkVerifyRequest>;

/** Inline body of `POST /auth/verify-email` (not a named component). */
export const VerifyEmailRequest = z.object({ token: z.string().min(16) });
export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequest>;

/** Inline body of `POST /auth/password-reset/confirm` (not a named component). */
export const PasswordResetConfirmRequest = z.object({
  token: z.string().min(16),
  password: z.string().min(10).max(256),
});
export type PasswordResetConfirmRequest = z.infer<typeof PasswordResetConfirmRequest>;

export const Session = z.object({
  user: User,
  session_expires_at: Timestamp,
});
export type Session = z.infer<typeof Session>;

export const AuthProvider = z.object({
  id: z.string(),
  kind: z.enum(['oidc']),
  name: z.string(),
  start_url: Uri,
});
export type AuthProvider = z.infer<typeof AuthProvider>;

export const AuthProviders = z.object({
  password: z.boolean(),
  magic_link: z.boolean(),
  signup: z.boolean(),
  providers: z.array(AuthProvider),
});
export type AuthProviders = z.infer<typeof AuthProviders>;

export const AuthKind = z.enum(['session', 'personal_access_token']);
export type AuthKind = z.infer<typeof AuthKind>;

export const WorkspaceMembership = z.object({
  workspace: Workspace,
  member_id: Id,
  role: Role,
  effective_mask: PermissionMask,
  status: MemberStatus.optional(),
});
export type WorkspaceMembership = z.infer<typeof WorkspaceMembership>;

export const Me = z.object({
  user: User,
  memberships: z.array(WorkspaceMembership),
  auth_kind: AuthKind.optional(),
});
export type Me = z.infer<typeof Me>;

/** Inline response of `POST /auth/ws-token` (not a named component). */
export const WsTokenResponse = z.object({
  token: z.string(),
  expires_at: Timestamp,
  ws_url: Uri,
});
export type WsTokenResponse = z.infer<typeof WsTokenResponse>;

export const PersonalAccessToken = z.object({
  id: Id,
  name: z.string().max(120),
  workspace_id: Id,
  permission_mask: PermissionMask,
  prefix: z.string(),
  last_used_at: Timestamp.nullable().optional(),
  expires_at: Timestamp.nullable().optional(),
  created_at: Timestamp,
});
export type PersonalAccessToken = z.infer<typeof PersonalAccessToken>;

export const PersonalAccessTokenCreate = z.object({
  name: z.string().min(1).max(120),
  workspace_id: Id,
  permission_mask: PermissionMask,
  expires_at: Timestamp.nullable().optional(),
});
export type PersonalAccessTokenCreate = z.infer<typeof PersonalAccessTokenCreate>;

export const PersonalAccessTokenCreated = PersonalAccessToken.extend({
  secret: z.string(),
});
export type PersonalAccessTokenCreated = z.infer<typeof PersonalAccessTokenCreated>;
